
import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { v4 as uuidv4 } from "uuid";
import { Info, Key, Link, Plug, Server, User } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { inverterManufacturers, ApiFormValues, InverterApi } from "@/types/inverterApi";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

const formSchema = z.object({
  manufacturer: z.string().min(1, { message: "Selecione o fabricante" }),
  name: z.string().min(3, { message: "Nome da API deve ter pelo menos 3 caracteres" }),
  baseUrl: z.string().url({ message: "URL base deve ser uma URL válida" }),
  apiKey: z.string().min(1, { message: "Chave da API é obrigatória" }),
  username: z.string().optional(),
  password: z.string().optional(),
  isActive: z.boolean().default(true),
  notes: z.string().optional(),
});

interface InverterApiFormProps {
  onSuccess?: (api: InverterApi) => void;
  initialData?: ApiFormValues;
  isEdit?: boolean;
}

const InverterApiForm = ({ onSuccess, initialData, isEdit = false }: InverterApiFormProps) => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);

  const defaultValues: Partial<ApiFormValues> = {
    manufacturer: "",
    name: "",
    baseUrl: "",
    apiKey: "",
    username: "",
    password: "",
    isActive: true,
    notes: "",
    ...initialData,
  };

  const form = useForm<ApiFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  const onSubmit = async (values: ApiFormValues) => {
    setIsLoading(true);
    try {
      // Simulating API request
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const newApi: InverterApi = {
        id: isEdit ? (initialData as any).id : uuidv4(),
        ...values,
        createdAt: isEdit ? (initialData as any).createdAt : new Date().toISOString(),
      };
      
      // In a real app, you would save this to a database
      // For now, we'll just save to localStorage
      if (!isEdit) {
        const existingApis = JSON.parse(localStorage.getItem("inverterApis") || "[]");
        localStorage.setItem("inverterApis", JSON.stringify([...existingApis, newApi]));
      } else {
        const existingApis = JSON.parse(localStorage.getItem("inverterApis") || "[]");
        const updatedApis = existingApis.map((api: InverterApi) => 
          api.id === newApi.id ? newApi : api
        );
        localStorage.setItem("inverterApis", JSON.stringify(updatedApis));
      }
      
      toast({
        title: isEdit ? "API atualizada" : "API cadastrada",
        description: `A API ${values.name} foi ${isEdit ? "atualizada" : "cadastrada"} com sucesso.`,
      });
      
      if (onSuccess) {
        onSuccess(newApi);
      }
      
      if (!isEdit) {
        form.reset(defaultValues);
      }
    } catch (error) {
      toast({
        title: "Erro",
        description: "Ocorreu um erro ao cadastrar a API.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>{isEdit ? "Editar API" : "Cadastrar Nova API"}</CardTitle>
        <CardDescription>
          Configure os dados de acesso à API do fabricante de inversores
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="manufacturer"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Fabricante</FormLabel>
                    <Select 
                      onValueChange={field.onChange} 
                      defaultValue={field.value} 
                      disabled={isLoading}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o fabricante" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {inverterManufacturers.map((manufacturer) => (
                          <SelectItem key={manufacturer} value={manufacturer}>
                            {manufacturer}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nome da API</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="Ex: Growatt API Residencial" 
                        {...field} 
                        disabled={isLoading}
                      />
                    </FormControl>
                    <FormDescription>
                      Um nome para identificar esta configuração
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="baseUrl"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>URL Base</FormLabel>
                  <FormControl>
                    <div className="flex items-center gap-2">
                      <Link className="shrink-0 text-muted-foreground" />
                      <Input 
                        placeholder="https://api.exemplo.com/v1" 
                        {...field} 
                        disabled={isLoading}
                      />
                    </div>
                  </FormControl>
                  <FormDescription>
                    URL base da API do fabricante
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="apiKey"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Chave da API</FormLabel>
                  <FormControl>
                    <div className="flex items-center gap-2">
                      <Key className="shrink-0 text-muted-foreground" />
                      <Input 
                        placeholder="sua-chave-de-api" 
                        {...field} 
                        disabled={isLoading}
                      />
                    </div>
                  </FormControl>
                  <FormDescription>
                    Chave de autenticação para acessar a API
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="username"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Usuário (opcional)</FormLabel>
                    <FormControl>
                      <div className="flex items-center gap-2">
                        <User className="shrink-0 text-muted-foreground" />
                        <Input 
                          placeholder="usuário" 
                          {...field} 
                          value={field.value || ""} 
                          disabled={isLoading}
                        />
                      </div>
                    </FormControl>
                    <FormDescription>
                      Nome de usuário se necessário
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Senha (opcional)</FormLabel>
                    <FormControl>
                      <Input 
                        type="password" 
                        placeholder="senha" 
                        {...field} 
                        value={field.value || ""} 
                        disabled={isLoading}
                      />
                    </FormControl>
                    <FormDescription>
                      Senha se necessário
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Observações</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Informações adicionais sobre esta API" 
                      {...field} 
                      value={field.value || ""} 
                      disabled={isLoading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="isActive"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      disabled={isLoading}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Ativar API</FormLabel>
                    <FormDescription>
                      Marque para ativar a integração com esta API
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-2">
              <Button type="submit" disabled={isLoading}>
                {isLoading ? "Salvando..." : isEdit ? "Atualizar API" : "Cadastrar API"}
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default InverterApiForm;
